import { useState } from 'react';
import {
  AlertTriangle,
  Bell,
  Check,
  Copy,
  Edit3,
  Info,
  MoreVertical,
  Plus,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react';
import {
  useWebhooks,
  useWebhookEvents,
  useCreateWebhook,
  useUpdateWebhook,
  useDeleteWebhook,
  useTestWebhook,
  useRotateWebhookSecret,
} from '../../hooks/useWebhooks';
import { ConfirmDialog } from '../common/ConfirmDialog';
import type { Webhook, CreateWebhookRequest, UpdateWebhookRequest } from '../../types/webhook';
import { Badge, Button, EmptyState, Field, Input } from '../../ui';
import './WebhooksSettings.css';

interface WebhooksSettingsProps {
  projectId: string;
  userRole?: 'viewer' | 'member' | 'admin' | 'owner';
}

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; webhook: Webhook }
  | { type: 'delete'; webhook: Webhook }
  | { type: 'secret'; webhook: Webhook; secret?: string }
  | { type: 'rotate-confirm'; webhook: Webhook };

export function WebhooksSettings({ projectId, userRole = 'member' }: WebhooksSettingsProps) {
  const { data: webhooks, isLoading, error, refetch } = useWebhooks(projectId);
  const { data: eventsData } = useWebhookEvents(projectId);
  const createWebhook = useCreateWebhook();
  const updateWebhook = useUpdateWebhook();
  const deleteWebhook = useDeleteWebhook();
  const testWebhook = useTestWebhook();
  const rotateSecret = useRotateWebhookSecret();

  const [modalState, setModalState] = useState<ModalState>({ type: 'none' });
  const [testingWebhookId, setTestingWebhookId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const canManage = userRole === 'admin' || userRole === 'owner';

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleCreate = async (data: CreateWebhookRequest) => {
    try {
      const result = await createWebhook.mutateAsync({ projectId, data });
      setModalState({ type: 'secret', webhook: result, secret: result.secret });
      showSuccess('Webhook created successfully');
    } catch (err) {
      console.error('Failed to create webhook:', err);
    }
  };

  const handleUpdate = async (webhookId: string, data: UpdateWebhookRequest) => {
    try {
      await updateWebhook.mutateAsync({ projectId, webhookId, data });
      setModalState({ type: 'none' });
      showSuccess('Webhook updated successfully');
    } catch (err) {
      console.error('Failed to update webhook:', err);
    }
  };

  const handleDelete = async (webhookId: string) => {
    try {
      await deleteWebhook.mutateAsync({ projectId, webhookId });
      setModalState({ type: 'none' });
      showSuccess('Webhook deleted successfully');
    } catch (err) {
      console.error('Failed to delete webhook:', err);
    }
  };

  const handleTest = async (webhookId: string) => {
    setTestingWebhookId(webhookId);
    try {
      await testWebhook.mutateAsync({ projectId, webhookId });
      showSuccess('Test webhook sent');
    } catch (err) {
      console.error('Failed to send test webhook:', err);
    } finally {
      setTestingWebhookId(null);
    }
  };

  const handleRotateSecret = async (webhook: Webhook) => {
    try {
      const result = await rotateSecret.mutateAsync({ projectId, webhookId: webhook.id });
      setModalState({ type: 'secret', webhook, secret: result.secret });
      showSuccess('Secret rotated successfully');
    } catch (err) {
      console.error('Failed to rotate secret:', err);
    }
  };

  const handleToggleEnabled = async (webhook: Webhook) => {
    try {
      await updateWebhook.mutateAsync({
        projectId,
        webhookId: webhook.id,
        data: { enabled: !webhook.enabled },
      });
    } catch (err) {
      console.error('Failed to toggle webhook:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="webhooks-settings">
        <div className="webhooks-settings__loading">
          <div className="spinner" />
          <p>Loading webhooks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="webhooks-settings">
        <div className="webhooks-settings__error">
          <p>Failed to load webhooks</p>
          <p className="webhooks-settings__error-detail">{error.message}</p>
          <Button variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="webhooks-settings">
      <div className="webhooks-settings__header">
        <div className="webhooks-settings__header-content">
          <h3>Webhooks</h3>
          <p className="webhooks-settings__muted">
            Configure webhooks to receive real-time notifications when events occur in your project.
          </p>
        </div>
        {canManage && (
          <Button
            leftIcon={<Plus size={14} aria-hidden="true" />}
            onClick={() => setModalState({ type: 'create' })}
          >
            Add Webhook
          </Button>
        )}
      </div>

      {successMessage && (
        <div className="webhooks-settings__success">
          <Check size={14} aria-hidden="true" />
          {successMessage}
        </div>
      )}

      {webhooks && webhooks.length === 0 ? (
        <EmptyState
          icon={<Bell size={20} />}
          title="No webhooks configured"
          description="Add a webhook to receive notifications when events occur."
          action={
            canManage ? (
              <Button onClick={() => setModalState({ type: 'create' })}>
                Add Your First Webhook
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="webhooks-list">
          {webhooks?.map(webhook => (
            <WebhookCard
              key={webhook.id}
              webhook={webhook}
              canManage={canManage}
              isTesting={testingWebhookId === webhook.id}
              onEdit={() => setModalState({ type: 'edit', webhook })}
              onDelete={() => setModalState({ type: 'delete', webhook })}
              onTest={() => handleTest(webhook.id)}
              onRotateSecret={() => setModalState({ type: 'rotate-confirm', webhook })}
              onToggleEnabled={() => handleToggleEnabled(webhook)}
            />
          ))}
        </div>
      )}

      {!canManage && (
        <div className="webhooks-settings__readonly-notice">
          <Info size={14} aria-hidden="true" />
          <span>You need admin permissions to manage webhooks.</span>
        </div>
      )}

      {modalState.type === 'create' && (
        <WebhookFormModal
          title="Create Webhook"
          eventsData={eventsData}
          isLoading={createWebhook.isPending}
          error={createWebhook.error?.message}
          onSubmit={handleCreate}
          onClose={() => setModalState({ type: 'none' })}
        />
      )}

      {modalState.type === 'edit' && (
        <WebhookFormModal
          title="Edit Webhook"
          webhook={modalState.webhook}
          eventsData={eventsData}
          isLoading={updateWebhook.isPending}
          error={updateWebhook.error?.message}
          onSubmit={data => handleUpdate(modalState.webhook.id, data as UpdateWebhookRequest)}
          onClose={() => setModalState({ type: 'none' })}
        />
      )}

      {modalState.type === 'delete' && (
        <ConfirmDialog
          title="Delete Webhook"
          message={`Are you sure you want to delete this webhook? This will also delete all delivery history.`}
          confirmLabel="Delete"
          confirmVariant="danger"
          isLoading={deleteWebhook.isPending}
          onConfirm={() => handleDelete(modalState.webhook.id)}
          onCancel={() => setModalState({ type: 'none' })}
        />
      )}

      {modalState.type === 'rotate-confirm' && (
        <ConfirmDialog
          title="Rotate Signing Secret"
          message="This will generate a new signing secret. Your existing webhook verification code will stop working until you update it with the new secret."
          confirmLabel="Rotate Secret"
          confirmVariant="danger"
          isLoading={rotateSecret.isPending}
          onConfirm={() => handleRotateSecret(modalState.webhook)}
          onCancel={() => setModalState({ type: 'none' })}
        />
      )}

      {modalState.type === 'secret' && modalState.secret && (
        <SecretDisplayModal
          secret={modalState.secret}
          onClose={() => setModalState({ type: 'none' })}
        />
      )}
    </div>
  );
}

interface WebhookCardProps {
  webhook: Webhook;
  canManage: boolean;
  isTesting: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onTest: () => void;
  onRotateSecret: () => void;
  onToggleEnabled: () => void;
}

function WebhookCard({
  webhook,
  canManage,
  isTesting,
  onEdit,
  onDelete,
  onTest,
  onRotateSecret,
  onToggleEnabled,
}: WebhookCardProps) {
  const [showActions, setShowActions] = useState(false);

  return (
    <div
      className={`webhook-card ${
        webhook.enabled ? 'webhook-card--enabled' : 'webhook-card--disabled'
      }`}
    >
      <div className="webhook-card__header">
        <div className="webhook-card__url-container">
          <span
            className={`webhook-card__status ${
              webhook.enabled ? 'webhook-card__status--active' : 'webhook-card__status--inactive'
            }`}
          />
          <span className="webhook-card__url">{webhook.url}</span>
        </div>
        {canManage && (
          <div className="webhook-card__actions-menu">
            <button
              type="button"
              className="webhook-card__menu-trigger"
              onClick={() => setShowActions(!showActions)}
              aria-label="More actions"
            >
              <MoreVertical size={18} aria-hidden="true" />
            </button>
            {showActions && (
              <>
                <div
                  className="webhook-card__actions-backdrop"
                  onClick={() => setShowActions(false)}
                />
                <div className="webhook-card__actions-dropdown">
                  <button
                    type="button"
                    onClick={() => {
                      onEdit();
                      setShowActions(false);
                    }}
                  >
                    <Edit3 size={14} aria-hidden="true" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onTest();
                      setShowActions(false);
                    }}
                    disabled={isTesting}
                  >
                    <Send size={14} aria-hidden="true" />
                    {isTesting ? 'Sending...' : 'Send Test'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onRotateSecret();
                      setShowActions(false);
                    }}
                  >
                    <RefreshCw size={14} aria-hidden="true" />
                    Rotate Secret
                  </button>
                  <hr />
                  <button
                    type="button"
                    className="webhook-card__menu-danger"
                    onClick={() => {
                      onDelete();
                      setShowActions(false);
                    }}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {webhook.description && <p className="webhook-card__description">{webhook.description}</p>}

      <div className="webhook-card__events">
        <span className="webhook-card__events-label">Events:</span>
        <div className="webhook-card__events-list">
          {webhook.events.slice(0, 3).map(event => (
            <Badge key={event} tone="neutral">
              {event}
            </Badge>
          ))}
          {webhook.events.length > 3 && (
            <Badge tone="neutral">+{webhook.events.length - 3} more</Badge>
          )}
        </div>
      </div>

      <div className="webhook-card__footer">
        <div className="webhook-card__stats">
          <span className="webhook-card__stat">
            <span className="webhook-card__stat-value">{webhook.delivery_stats.total}</span> total
          </span>
          <span className="webhook-card__stat webhook-card__stat--success">
            <span className="webhook-card__stat-value">{webhook.delivery_stats.successful}</span>{' '}
            success
          </span>
          <span className="webhook-card__stat webhook-card__stat--failed">
            <span className="webhook-card__stat-value">{webhook.delivery_stats.failed}</span> failed
          </span>
        </div>

        {canManage && (
          <label className="webhook-toggle">
            <input type="checkbox" checked={webhook.enabled} onChange={onToggleEnabled} />
            <span className="webhook-toggle__slider" />
          </label>
        )}
      </div>
    </div>
  );
}

interface WebhookFormModalProps {
  title: string;
  webhook?: Webhook;
  eventsData?: {
    events: string[];
    categories: Record<string, { type: string; description: string }[]>;
  };
  isLoading: boolean;
  error?: string;
  onSubmit: (data: CreateWebhookRequest) => Promise<void> | void;
  onClose: () => void;
}

function WebhookFormModal({
  title,
  webhook,
  eventsData,
  isLoading,
  error,
  onSubmit,
  onClose,
}: WebhookFormModalProps) {
  const [url, setUrl] = useState(webhook?.url || '');
  const [description, setDescription] = useState(webhook?.description || '');
  const [selectedEvents, setSelectedEvents] = useState<string[]>(webhook?.events || []);
  const [urlError, setUrlError] = useState<string | null>(null);

  const validateUrl = (value: string) => {
    if (!value) {
      setUrlError('URL is required');
      return false;
    }
    if (!value.startsWith('https://')) {
      setUrlError('URL must start with https://');
      return false;
    }
    try {
      new URL(value);
      setUrlError(null);
      return true;
    } catch {
      setUrlError('Invalid URL format');
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateUrl(url)) return;
    if (selectedEvents.length === 0) return;

    const data: CreateWebhookRequest = {
      url,
      description: description || undefined,
      events: selectedEvents,
    };

    onSubmit(data);
  };

  const toggleEvent = (event: string) => {
    setSelectedEvents(prev =>
      prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]
    );
  };

  const toggleCategory = (categoryEvents: string[]) => {
    const allSelected = categoryEvents.every(e => selectedEvents.includes(e));
    if (allSelected) {
      setSelectedEvents(prev => prev.filter(e => !categoryEvents.includes(e)));
    } else {
      setSelectedEvents(prev => [...new Set([...prev, ...categoryEvents])]);
    }
  };

  const selectAllEvents = () => {
    if (eventsData?.events) {
      setSelectedEvents(eventsData.events);
    }
  };

  const clearAllEvents = () => {
    setSelectedEvents([]);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--webhook-form" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>{title}</h2>
          <button className="modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="modal__body">
            {error && <div className="webhooks-settings__form-error">{error}</div>}

            <Field
              label="Endpoint URL"
              htmlFor="webhook-url"
              hint="Must be a valid HTTPS URL"
              error={urlError || undefined}
            >
              <Input
                id="webhook-url"
                type="url"
                value={url}
                onChange={e => {
                  setUrl(e.target.value);
                  validateUrl(e.target.value);
                }}
                placeholder="https://example.com/webhooks"
                required
                invalid={!!urlError}
              />
            </Field>

            <Field label="Description (optional)" htmlFor="webhook-description">
              <Input
                id="webhook-description"
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Production webhook endpoint"
              />
            </Field>

            <div className="webhooks-settings__events-block">
              <div className="webhooks-settings__events-header">
                <label>Events to Subscribe</label>
                <div className="webhooks-settings__events-actions">
                  <button
                    type="button"
                    className="webhooks-settings__link-btn"
                    onClick={selectAllEvents}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="webhooks-settings__link-btn"
                    onClick={clearAllEvents}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="webhook-events-selector">
                {eventsData?.categories &&
                  Object.entries(eventsData.categories).map(([category, events]) => (
                    <div key={category} className="webhook-events-category">
                      <div className="webhook-events-category__header">
                        <label className="webhook-events-category__toggle">
                          <input
                            type="checkbox"
                            checked={events.every(e => selectedEvents.includes(e.type))}
                            onChange={() => toggleCategory(events.map(e => e.type))}
                          />
                          <span className="webhook-events-category__name">
                            {formatCategoryName(category)}
                          </span>
                        </label>
                      </div>
                      <div className="webhook-events-category__events">
                        {events.map(event => (
                          <label key={event.type} className="webhook-event-item">
                            <input
                              type="checkbox"
                              checked={selectedEvents.includes(event.type)}
                              onChange={() => toggleEvent(event.type)}
                            />
                            <span className="webhook-event-item__type">{event.type}</span>
                            <span className="webhook-event-item__description">
                              {event.description}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
              {selectedEvents.length === 0 && (
                <span className="webhooks-settings__inline-error">Select at least one event</span>
              )}
            </div>
          </div>

          <footer className="modal__footer">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || selectedEvents.length === 0 || !!urlError}
              loading={isLoading}
            >
              {isLoading ? 'Saving...' : webhook ? 'Save Changes' : 'Create Webhook'}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}

interface SecretDisplayModalProps {
  secret: string;
  onClose: () => void;
}

function SecretDisplayModal({ secret, onClose }: SecretDisplayModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--secret" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Webhook Signing Secret</h2>
          <button className="modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </header>

        <div className="modal__body">
          <div className="webhooks-settings__secret-warning">
            <AlertTriangle size={20} aria-hidden="true" />
            <p>
              <strong>Save this secret now!</strong> You won't be able to see it again. Use this
              secret to verify webhook signatures.
            </p>
          </div>

          <div className="webhooks-settings__secret-value">
            <code>{secret}</code>
            <button
              type="button"
              className="webhooks-settings__copy-btn"
              onClick={handleCopy}
              title="Copy to clipboard"
              aria-label="Copy secret to clipboard"
            >
              {copied ? (
                <Check size={14} aria-hidden="true" />
              ) : (
                <Copy size={14} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        <footer className="modal__footer">
          <Button onClick={onClose}>I've Saved the Secret</Button>
        </footer>
      </div>
    </div>
  );
}

function formatCategoryName(category: string): string {
  return category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
